import { Directive, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { fromEvent, Subject, takeUntil, throttleTime, debounceTime, merge, tap } from 'rxjs';
import { ChatButtonService } from 'src/app/core/services';

@Directive({
  selector: '[scrollTracker]',
  standalone: true
})
export class ScrollTrackerDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private contentResizeObserver: ResizeObserver | null = null;

  constructor(private elementRef: ElementRef,
    private chatButtonService: ChatButtonService
  ) {}

  ngOnInit() {
    const element = this.elementRef.nativeElement;
    const scrollEvent$ = fromEvent(element, 'scroll');
    const resizeEvent$ = fromEvent(window, 'resize');

    // This directive lives once on the app-shell's mat-sidenav-content and persists across
    // route changes, so scroll/window-resize alone miss any route whose OWN content height
    // changes asynchronously without either of those firing (e.g. an embedded widget that
    // mounts after an API call, images loading, etc.) — the footer moves, but nothing here
    // would know to recheck. A ResizeObserver on the actual content wrapper reacts to real
    // height changes regardless of what caused them, for any route, without each route having
    // to remember to call checkFooterPosition() itself.
    const contentResize$ = new Subject<void>();
    const contentElement = element.querySelector('.component-container') as HTMLElement | null;
    if (contentElement) {
      this.contentResizeObserver = new ResizeObserver(() => contentResize$.next());
      this.contentResizeObserver.observe(contentElement);
    }

    // Throttled events (during scroll/resize/content-resize)
    merge(scrollEvent$, resizeEvent$, contentResize$)
      .pipe(
        throttleTime(50),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.chatButtonService.checkFooterPosition());

    // Debounced events (after scroll/resize/content-resize ends)
    merge(scrollEvent$, resizeEvent$, contentResize$)
      .pipe(
        debounceTime(150),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.chatButtonService.checkFooterPosition());

      this.chatButtonService.checkFooterPosition()
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.contentResizeObserver?.disconnect();
  }
}